
import React, { forwardRef } from "react";
import { 
  AlertCircle, 
  AlertTriangle, 
  CheckCircle, 
  ShieldCheck, 
  Star
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type ReliabilityLevel = 1 | 2 | 3 | 4 | 5;

interface ReliabilityBadgeProps {
  level: ReliabilityLevel;
  className?: string;
  showLabel?: boolean;
}

const reliabilityConfig = {
  1: {
    label: "Alpha",
    description: "Version expérimentale, bugs fréquents, fonctionnalités limitées",
    icon: AlertCircle,
    variant: "destructive" as const,
    className: "bg-red-500 hover:bg-red-600",
  },
  2: {
    label: "Beta",
    description: "En développement actif, peut contenir des bugs importants",
    icon: AlertTriangle,
    variant: "default" as const,
    className: "bg-orange-500 hover:bg-orange-600",
  },
  3: {
    label: "Stable",
    description: "Fonctionnel avec quelques bugs mineurs possibles",
    icon: CheckCircle,
    variant: "default" as const,
    className: "bg-yellow-500 hover:bg-yellow-600",
  },
  4: {
    label: "Fiable",
    description: "Entièrement testé et fiable",
    icon: ShieldCheck,
    variant: "default" as const,
    className: "bg-green-500 hover:bg-green-600",
  },
  5: {
    label: "Premium",
    description: "Version optimisée, performances maximales",
    icon: Star,
    variant: "default" as const,
    className: "bg-indigo-500 hover:bg-indigo-600",
  },
};

export const ReliabilityBadge = forwardRef<HTMLDivElement, ReliabilityBadgeProps>(
  ({ level, className, showLabel = false }, ref) => {
    const config = reliabilityConfig[level];
    const Icon = config.icon;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div ref={ref} className={cn("inline-flex", className)}>
              <Badge 
                variant={config.variant} 
                className={cn(
                  "px-2 py-1 h-6", 
                  config.className
                )}
              >
                <div className="flex items-center gap-1">
                  <Icon className="h-3.5 w-3.5" />
                  <span className="text-xs ml-1">{config.label}</span>
                </div>
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            <p className="font-medium">{config.label}</p>
            <p className="text-xs text-muted-foreground">{config.description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

ReliabilityBadge.displayName = "ReliabilityBadge";
